import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { useLocation } from 'react-router';

import { Button, Link, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useMutableCookiePrefs } from '#src/app/shared/hooks/session';

import { trackCookieWallClick, trackCookieWallView } from '../api/track';

type Props = {
	onAccept?: (consentText: string) => void;
	onReject?: (consentText: string) => void;
	onError?: () => void;
};

function useAcceptedPrefsObject() {
	const { translate } = useI18n();
	// the string displayed in the cookie wall
	const consentText = translate('fe.components.cookie_wall.accept_button');
	return useMemo(
		() => ({
			preferences: {
				consent_text: consentText,
				status: true,
			},
			analytics: {
				consent_text: consentText,
				status: true,
			},
			marketing: {
				consent_text: consentText,
				status: true,
			},
		}),
		[consentText],
	);
}

function useRejectedPrefsObject() {
	const { translate } = useI18n();
	// the string displayed in the cookie wall
	const consentText = translate('fe.components.cookie_wall.accept_button');
	return useMemo(
		() => ({
			preferences: {
				consent_text: consentText,
				status: false,
			},
			analytics: {
				consent_text: consentText,
				status: false,
			},
			marketing: {
				consent_text: consentText,
				status: false,
			},
		}),
		[consentText],
	);
}

function useAllPrefsObject() {
	const accepted = useAcceptedPrefsObject();
	const rejected = useRejectedPrefsObject();
	return useMemo(
		() => ({
			accepted,
			rejected,
		}),
		[accepted, rejected],
	);
}

// oxlint-disable-next-line max-lines-per-function
export const CookieWallContainer: React.FC<Props> = ({ onAccept, onReject, onError }) => {
	const track = useTracking();
	const location = useLocation();
	const wallEl = useRef<HTMLDivElement>(null);
	const { translate } = useI18n();
	// the string displayed in the cookie wall
	const consentText = translate('fe.components.cookie_wall.accept_button');
	const prefs = useAllPrefsObject();
	const [, saveCookiePrefs] = useMutableCookiePrefs();
	const [isExpanded, setIsExpanded] = useState(false);

	useEffect(() => {
		void trackCookieWallView(track);
	}, [track]);

	const handleAccept = useCallback(async () => {
		try {
			await saveCookiePrefs(prefs.accepted);
			onAccept?.(consentText);
			void trackCookieWallClick('accept', track);
			return true;
		} catch {
			onError?.();
			return false;
		}
	}, [saveCookiePrefs, prefs, consentText, track, onAccept, onError]);

	const handleReject = useCallback(async () => {
		try {
			await saveCookiePrefs(prefs.rejected);
			onReject?.(consentText);
			void trackCookieWallClick('reject', track);
			return true;
		} catch {
			onError?.();
			return false;
		}
	}, [saveCookiePrefs, prefs, consentText, track, onReject, onError]);

	const handleManage = useCallback(() => {
		void trackCookieWallClick('manage', track);
	}, [track]);

	return (
		<div
			// z-index is set to 4990 to be below modals (5000+) but above other content
			className="bg-fill-inverse-strong fixed bottom-0 left-0 z-4990 w-full shadow-[29px_-25px_23px_0_rgba(0,0,0,0.03),13px_-11px_17px_0_rgba(0,0,0,0.05),3px_-3px_9px_0_rgba(0,0,0,0.07)]"
			data-testid="cookie-wall-modal"
		>
			<div
				className="container flex max-w-4xl flex-col pt-6 pb-4 md:flex-row md:gap-10 md:pt-10 md:pb-10"
				ref={wallEl}
				// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
				role="dialog"
				aria-labelledby="cookie-wall-heading"
				aria-describedby="cookie-wall-description"
			>
				<div className="relative flex min-w-0 flex-1 flex-col">
					<Text variant="heading" size="small" as="h1" id="cookie-wall-heading">
						<Translate value="fe.components.cookie_wall.title" />
					</Text>
					<Text
						as="p"
						id="cookie-wall-description"
						className={cn('md:line-clamp-none', { 'line-clamp-4': !isExpanded })}
					>
						<Translate value="fe.components.cookie_wall.desc" />
					</Text>
					<button
						type="button"
						onClick={() => setIsExpanded(true)}
						className={cn('mt-2 self-end underline md:hidden', { hidden: isExpanded })}
						aria-expanded={isExpanded}
						aria-controls="cookie-wall-description"
						data-qa="cookie-wall-read-more"
					>
						<Translate value="fe.pages.common.read_more" />
					</button>
				</div>
				<div className="flex flex-col items-center gap-4 py-8 md:shrink-0">
					<Button
						variant="primary"
						size="small"
						className="w-full"
						onClick={handleAccept}
						data-testid="cookie-wall-accept"
						data-qa="cookie-wall-accept"
					>
						<Translate value="fe.components.cookie_wall.accept_button" />
					</Button>
					<Button
						variant="secondary"
						size="small"
						className="w-full"
						onClick={handleReject}
						data-testid="cookie-wall-reject"
						data-qa="cookie-wall-reject"
					>
						<Translate value="fe.components.cookie_wall.reject_button" />
					</Button>
					<Link
						href={`/account_settings/manage_cookies?redirect_to=${encodeURIComponent(
							location.pathname + location.search + location.hash,
						)}`}
						data-pass-thru
						onClick={handleManage}
						data-testid="cookie-wall-manage"
						data-qa="cookie-wall-manage"
					>
						<Translate value="fe.components.cookie_wall.manage_cookies_button" />
					</Link>
				</div>
			</div>
		</div>
	);
};
